import { type ExtensionApi } from '../../../core/extension';
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'dynamicSankey',
  name_en: 'Dynamic Sankey',
  name_zh: '动态桑基图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {},
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    name: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    value: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    valueText: {
      itemType: [DEFINE.STR, DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    target: {
      itemType: [DEFINE.ARRAY],
      category: DEFINE.SINGLE
    },
    date: {
      itemType: [DEFINE.STR, DEFINE.DATE],
      category: DEFINE.SINGLE
    },
    describe: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    unit: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    otherInfo: {
      itemType: [DEFINE.ARRAY],
      category: DEFINE.SINGLE
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    name: string;
    value: string;
    valueText: string;
    target: string;
    date: string;
    describe: string;
    unit: string;
    otherInfo: string;
  };
  animationDuration: number;
}

/**
 * 解析处理数据
 * 对数据进行排序、过滤、转换、生成日期数据等
 * @param api
 * @param spec
 * @returns
 */
function processData(api: ExtensionApi, spec: Spec) {
  const { util } = api;
  const sourceData = api.data[0].values;
  const { encoding } = spec;
  const { dates } = util.dataEncoding.encoding(sourceData, {
    dates: {
      field: 'date',
      transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
    }
  });
  const nodes = dates.map(ele => {
    return sourceData
      .filter(item => item[encoding.date] === ele)
      .map(item => {
        return {
          depth: item.depth,
          root: item.root,
          itemStyle: item.itemStyle,
          name: item[encoding.name],
          num: item[encoding.value],
          numText: item[encoding.valueText],
          unit: item[encoding.unit],
          otherInfo: item[encoding.otherInfo]
        };
      });
  });
  const links = dates.map(ele => {
    return sourceData
      .filter(item => item[encoding.date] === ele)
      .map(item => {
        return item[encoding.target].map(element => {
          return {
            source: item[encoding.name],
            target: element.name,
            value: element.value
          };
        });
      })
      .flat(Infinity);
  });
  const infos = dates
    .map(ele => {
      const array = sourceData
        .filter(item => item[encoding.date] === ele)
        .map(item => {
          return {
            text: item[encoding.describe]
          };
        });
      // 数组对象去重
      const uniqueMap = new Map();
      array.forEach(item => {
        if (item && item.text !== undefined) {
          uniqueMap.set(item.text, item);
        }
      });
      return Array.from(uniqueMap.values());
    })
    .flat(Infinity);
  return {
    nodes,
    links,
    dates,
    infos
  };
}

/**
 * option配置
 * @returns
 */
function defaultOption() {
  return {
    series: [
      {
        type: 'sankey',
        id: 'treeSankey',
        universalTransition: false,
        animation: true,
        dvLayout: 'tree',
        tooltip: {
          formatter: []
        },
        data: [],
        links: [],
        top: 30,
        bottom: 30,
        left: 120,
        right: 120
      }
    ]
  };
}
/**
 * 处理option中数据配置
 * @returns
 */
function handleOptionData(
  nodes: any,
  links: any,
  infos: any,
  seq: number,
  dom: any,
  renderApi: any
) {
  const option = defaultOption();
  const showTextArr = { param1: 'numText', param2: 'unit', param3: 'num' };
  const index = seq;

  option.series[0].links = new ThsDataVStandardChart.helper.sankeyHelper.handleLinks(
    nodes[index],
    links[index]
  );

  option.series[0].data = nodes[index].map(item => {
    return {
      ...item,
      ...handleFormatter(
        {
          width: dom.getBoundingClientRect().width - 12,
          text: infos[index].text,
          root: nodes[index].filter(ele => ele.root)[0]
        },
        item,
        showTextArr
      )
    };
  });

  option.series[0].tooltip.formatter = handleTooltip();
  option.series[0].tooltip.extraCssText = 'background: #3b3b3b;';
  option.series[0].dvNodeLengthPercent =
    new ThsDataVStandardChart.helper.sankeyHelper.handleNodeLengthPercent(nodes, {
      min: 0.33,
      max: 0.66,
      type: 'linear'
    })[index];
  const renderParams = {
    option: renderApi.mergeChartOptionProxy(option)
  };
  renderApi.optionCallbackProxy(renderParams.option);
  return renderParams;
}
export const dynamicSankey = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  // 必须加上这个，否则生成timeline实例会失败
  withTimeline: true,
  visInfo,
  render(api, spec, $dom) {
    // * -------------------------- data
    const { dates, nodes, links, infos } = processData(api, spec);
    // * -------------------------- chart
    // * -------------------------- timeline
    return renderApi => {
      const renderParams = handleOptionData(nodes, links, infos, nodes.length - 1, $dom, renderApi);

      const timelineIns = renderApi.renderTimelineProxy(dates as string[]);
      // renderApi.renderLegendProxy(renderParams.option.legend.data);
      // TODO:显示的是黑色风格？
      const chartIns = renderApi.renderChartProxy(renderParams, { theme: 'pc-app-light' });
      timelineIns?.on?.('change', e => {
        chartIns.play(handleOptionData(nodes, links, infos, e.index, $dom, renderApi));
      });
    };
  }
});

/**
 * 处理节点文本
 * @param arg1 根节点额外信息
 * @param arg2 节点信息
 * @param arg3 节点信息属性字段(方便业务自定义展示属性)
 * @param theme 主题
 * @returns
 */
function handleFormatter(arg1: Arg1, arg2, arg3) {
  const defaultArg3 = {
    param1: 'numText',
    param2: 'unit',
    param3: 'num'
  };
  const attrKey = Object.assign(defaultArg3, arg3);
  let rich = {};
  const labelFunction = () => {
    let formatter;
    if (arg2.root == true) {
      // 处理根节点的label
      const rootWidth = 170;
      const rootImageWidth = 16;
      const textPadding = 3;
      const ellipsisWidth = 16;
      const showLabelNum = 0;
      const dvTextWdith = measureTextWidth(arg1.text, 12);
      if (arg1.width / 4 <= rootWidth) {
        const textContentWidth =
          rootWidth - rootImageWidth - textPadding - dvTextWdith - ellipsisWidth;
      }

      const array = [];
      const name = `{a|${arg1.text}}`;
      const number = `{b|${arg2.name}}{c|${arg2[attrKey.param1]}${arg2[attrKey.param2]}}`;
      array.push(name);
      array.push(number);
      formatter = array.join('\n');
    } else {
      // 处理其他节点的label
      let showLabelNum = 0;
      const otherNameWdith = measureTextWidth(arg2.name, 12);
      if (otherNameWdith > 72) {
        const dvNameArray = arg2.name.split('');
        for (let i = 0; i < dvNameArray.length; i++) {
          const w = measureTextWidth(arg2.name.slice(0, i + 1), 12);
          if (w > 60) {
            showLabelNum = i;
            break;
          }
        }
      }
      const array = [];
      const name = `{a|${
        showLabelNum === 0 ? arg2.name : `${arg2.name.slice(0, showLabelNum)}...`
      }}`;
      const number = `{b|${arg2[attrKey.param1]}${arg2[attrKey.param2]}}`;
      array.push(name);
      array.push(number);
      formatter = array.join('\n');
    }
    return formatter;
  };
  if (arg2.root == true) {
    rich = {
      a: {
        color: 'rgba(0,0,0,0.60)',
        fontSize: 14,
        height: 8,
        align: 'center',
        padding: [2, 0, 0, 2]
      },
      b: {
        color: 'rgba(0,0,0,0.84)',
        padding: [12, 0, 0, 0],
        fontSize: 16,
        height: 12,
        align: 'center',
        fontWeight: 600
      },
      c: {
        color: arg2.itemStyle.color,
        fontSize: 16,
        fontWeight: 600,
        height: 10,
        verticalAlign: 'bottom',
        align: 'center',
        padding: [0, 2, 0, 1]
      }
    };
  } else {
    rich = {
      a: {
        color: 'rgba(0,0,0,0.84)',
        fontWeight: '600',
        fontSize: 12,
        align: arg2.depth > arg1.root.depth ? 'left' : 'right',
        padding: arg2.num < 0 ? [1, 0, 0, 0] : [0, 0, 0, 0]
      },
      b: {
        color: arg2.num < 0 ? 'rgba(0,0,0,0.84)' : arg2.itemStyle.color,
        padding: arg2.num < 0 ? [4, 0, -1.5, 0] : [4, 0, -2, 0],
        fontSize: 14,
        fontWeight: 500,
        align: arg2.depth > arg1.root.depth ? 'left' : 'right',
        verticalAlign: 'bottom'
      }
    };
  }
  return {
    label: {
      formatter: labelFunction,
      rich
    }
  };
}

/**
 *
 * @param opts 节点信息
 * @param arg1 节点信息属性字段(方便业务自定义展示属性)
 * @returns
 */
function handleTooltip() {
  return params => {
    const { data } = params;
    let title = '';
    let content = [];
    if (data) {
      title = `<div style="font-size: 14px; color: rgba(255,255,255,0.84); line-height: 16px;">
        <span style="margin-right: 8px;">${data.name}</span>
        <span style="margin-left: auto; color: ${params.color};">${
          data.numText ? data.numText : data.num
        }${data.unit ? data.unit : ''}</span>
      </div>`;
      content = data.otherInfo
        ? data.otherInfo.map(item => {
            return `<div style="display: flex;justify-content: space-between;align-items: center; margin-top: 8px;">
            <span style="margin-right: 8px;">${item.name}</span>
            <span style="margin-left: auto;">${item.value}${item.unit}</span>
        </div>`;
          })
        : [];
    } else {
      return null;
    }

    return `<div style="width:max-width;">
      <div style="margin: 0px 0 0; line-height: 1">
        ${title}
        ${
          content && content.length > 0
            ? `
          <div style="margin-top: 7px; border-top: 1px solid rgba(255,255,255,0.08); color: rgba(255,255,255,0.84);">
            ${content.join('')}
          </div>
        `
            : ''
        }
      </div>
    </div>`;
  };
}
function measureTextWidth(text, fontSize) {
  // 创建一个临时的 canvas 元素
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  // 设置字体大小
  context.font = `${fontSize}px sans-serif`;

  // 测量文本宽度
  const { width } = context.measureText(text);

  // 清理临时的 canvas 元素
  canvas.remove();

  return width;
}
